import { Shield, AlertTriangle, Brain, TrendingUp, Activity, Target, Zap, BarChart3 } from "lucide-react";

export interface BehavioralMetrics {
  panic_sells: number;
  hype_buys: number;
  trend_chasing: number;
  overtrading_rounds: number;
  total_buys: number;
  total_sells: number;
  sells_during_fear: number;
  buys_during_greed: number;
  max_concentration_pct: number;
  rounds_undiversified: number;
  biggest_single_loss: number;
  held_through_dip: number;
  trades_per_round: Array<{ round: number; count: number }>;
}

interface Props {
  metrics: BehavioralMetrics;
  maxRounds: number;
}

function getGrade(score: number): { label: string; color: string } {
  if (score >= 85) return { label: "A+", color: "text-emerald-400" };
  if (score >= 70) return { label: "A", color: "text-emerald-400" };
  if (score >= 55) return { label: "B", color: "text-accent" };
  if (score >= 40) return { label: "C", color: "text-amber-400" };
  if (score >= 25) return { label: "D", color: "text-orange-400" };
  return { label: "F", color: "text-red-400" };
}

const BehavioralReport = ({ metrics: m, maxRounds }: Props) => {
  const totalTrades = m.total_buys + m.total_sells;

  const panicScore = totalTrades > 0
    ? Math.max(0, 100 - (m.panic_sells / Math.max(1, m.total_sells)) * 200)
    : 100;

  const diversScore = maxRounds > 0
    ? Math.max(0, 100 - (m.rounds_undiversified / maxRounds) * 150)
    : 100;

  const concScore = m.max_concentration_pct > 0
    ? Math.max(0, 100 - Math.max(0, m.max_concentration_pct - 30) * 2)
    : 100;

  const emotionalScore = totalTrades > 0
    ? Math.max(0, 100 - ((m.hype_buys + m.panic_sells) / totalTrades) * 200)
    : 100;

  const overtradingScore = maxRounds > 0
    ? Math.max(0, 100 - (m.overtrading_rounds / maxRounds) * 300)
    : 100;

  const resilienceScore = m.held_through_dip > 0 ? Math.min(100, 50 + m.held_through_dip * 15) : 50;

  const overall = Math.round(
    (panicScore * 0.2 + diversScore * 0.2 + concScore * 0.15 + emotionalScore * 0.2 + overtradingScore * 0.1 + resilienceScore * 0.15)
  );

  const overallGrade = getGrade(overall);

  const categories = [
    { icon: Shield, name: "Panic Resistance", score: Math.round(panicScore), detail: m.panic_sells === 0 ? "Never panic sold — excellent composure!" : `${m.panic_sells} panic sell${m.panic_sells > 1 ? "s" : ""} during market fear`, warning: m.panic_sells > 2 },
    { icon: Target, name: "Diversification", score: Math.round(diversScore), detail: m.rounds_undiversified === 0 ? "Well diversified across sectors" : `${m.rounds_undiversified} round${m.rounds_undiversified > 1 ? "s" : ""} with poor sector spread`, warning: m.rounds_undiversified > 5 },
    { icon: BarChart3, name: "Concentration Risk", score: Math.round(concScore), detail: m.max_concentration_pct > 0 ? `Peak concentration: ${m.max_concentration_pct.toFixed(0)}% in one stock` : "No concentration issues detected", warning: m.max_concentration_pct > 50 },
    { icon: Brain, name: "Emotional Discipline", score: Math.round(emotionalScore), detail: m.hype_buys + m.panic_sells === 0 ? "Traded rationally without emotional bias" : `${m.hype_buys} hype buy${m.hype_buys !== 1 ? "s" : ""}, ${m.panic_sells} panic sell${m.panic_sells !== 1 ? "s" : ""}`, warning: m.hype_buys + m.panic_sells > 3 },
    { icon: Activity, name: "Trading Discipline", score: Math.round(overtradingScore), detail: m.overtrading_rounds === 0 ? "Healthy trading frequency" : `${m.overtrading_rounds} round${m.overtrading_rounds > 1 ? "s" : ""} with excessive trading`, warning: m.overtrading_rounds > 3 },
    { icon: Zap, name: "Market Resilience", score: Math.round(resilienceScore), detail: m.held_through_dip > 0 ? `Held through ${m.held_through_dip} recession round${m.held_through_dip > 1 ? "s" : ""}` : "No recessions encountered or no holdings during dips", warning: false },
  ];

  const traps: string[] = [];
  if (m.hype_buys > 2) traps.push("🎯 Buying after hype — you chased excitement over value");
  if (m.panic_sells > 1) traps.push("😰 Panic selling — fear drove your decisions during dips");
  if (m.trend_chasing > 2) traps.push("📈 Trend chasing — you bought stocks after big gains, risking entry at peaks");
  if (m.max_concentration_pct > 60) traps.push("⚠️ Overconcentration — too much wealth in a single stock");
  if (m.rounds_undiversified > 5) traps.push("🔀 Ignoring diversification — your portfolio lacked sector balance");
  if (m.overtrading_rounds > 3) traps.push("🔄 Overtrading — excessive buying and selling can erode returns");

  return (
    <div className="space-y-5">
      {/* Overall Score */}
      <div className="text-center py-4 border border-border rounded-xl bg-muted/30">
        <p className="text-muted-foreground text-xs mb-1">INVESTOR DISCIPLINE SCORE</p>
        <div className="flex items-center justify-center gap-3">
          <span className={`font-heading font-bold text-4xl ${overallGrade.color}`}>{overallGrade.label}</span>
          <span className="text-muted-foreground text-lg font-heading">{overall}/100</span>
        </div>
      </div>

      {/* Category Scores */}
      <div className="space-y-2">
        {categories.map((cat) => {
          const grade = getGrade(cat.score);
          const Icon = cat.icon;
          return (
            <div key={cat.name} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-muted/20">
              <Icon className={`w-4 h-4 ${cat.warning ? "text-amber-400" : "text-muted-foreground"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-foreground/80 text-sm font-heading font-semibold">{cat.name}</span>
                  <span className={`font-heading font-bold text-sm ${grade.color}`}>{cat.score}</span>
                </div>
                <p className="text-muted-foreground text-xs mt-0.5">{cat.detail}</p>
                <div className="h-1 rounded-full bg-muted/50 mt-1.5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      cat.score >= 70 ? "bg-emerald-400/60" : cat.score >= 40 ? "bg-amber-400/60" : "bg-red-400/60"
                    }`}
                    style={{ width: `${cat.score}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Behavioral Traps */}
      {traps.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span className="font-heading font-bold text-sm text-amber-400">Behavioral Traps Detected</span>
          </div>
          <div className="space-y-1.5">
            {traps.map((trap, i) => (
              <p key={i} className="text-muted-foreground text-xs bg-amber-400/5 border border-amber-400/10 rounded-lg px-3 py-2">
                {trap}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="p-2 rounded-lg bg-muted/30 border border-border/50">
          <p className="text-muted-foreground/50 text-[10px]">Total Trades</p>
          <p className="text-foreground/80 font-heading font-bold text-sm">{totalTrades}</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/30 border border-border/50">
          <p className="text-muted-foreground/50 text-[10px]">Buys in Greed</p>
          <p className="text-foreground/80 font-heading font-bold text-sm">{m.buys_during_greed}</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/30 border border-border/50">
          <p className="text-muted-foreground/50 text-[10px]">Biggest Loss</p>
          <p className="text-foreground/80 font-heading font-bold text-sm">{m.biggest_single_loss > 0 ? `-${m.biggest_single_loss}%` : "—"}</p>
        </div>
      </div>
    </div>
  );
};

export default BehavioralReport;
